import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import CuriRocketBigFlameSvg from '../../../assets/icons/CuriRocketBigFlameSvg';
import CuriRocketSvg from '../../../assets/icons/CuriRocketSvg';
import theme from '../../../assets/theme';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import { shuffleArray } from '../../../utils/arrayUtils';
import { LoadingDots } from '../../_atoms/DismissableQuestionList/DismissableQuestionList';

export const tips = [
  'Understand the different learning styles and needs of your students and adapt your teaching methods accordingly.',
  'An open question followed by a group discussion is a great way of exploring a topic in depth.',
  'Introducing a new topic? Start with a wordcloud and find out what your students already know.',
  'With Curipod you can use the AI generated lesson live in the classroom to spark engagement and creativity.',
  'Use the moderator tool during Live to filter out inappropriate responses.',
  'Create a positive and inclusive classroom environment where all students feel comfortable and respected.',
  'Use a variety of teaching methods, such as lectures, discussions, and hands-on activities, to keep students engaged and interested.',
  'Use a variety of Curipod activities, such as drawings, polls and open ended questions to keep students engaged and interested.',
  'Provide regular and timely feedback on student progress to help them see their strengths and areas for improvement.',
  'Encourage student participation and make sure every student has an opportunity to speak and share their ideas.',
  'Always fact check the content generated by the AI.',
  'Plan and execute lessons that are aligned with the curriculum and aligned with the standards and goals of the school/district.',
  'Build positive relationships with your students by getting to know them as individuals and showing genuine interest in their lives and experiences.',
  'Continuously reflect on your teaching practice. Look for ways to improve and grow as an educator. #growthmindset',
  'Be flexible and adaptable to meet the changing needs of your students, especially during unexpected events like a pandemic or inclement weather.',
  'Establish clear objectives for your lessons and make sure students understand what they are expected to learn.',
  'Differentiate instruction to meet the needs of diverse learners, such as providing additional support or enrichment activities.',
  'Use real-life examples and connections to make the material relevant and meaningful to students.',
  'Encourage critical thinking, problem-solving, and creativity in your instruction.',
  'An AI lesson should always be considered a draft that needs the human touch from the teacher.',
  'Incorporate group work and collaborative learning activities to promote socialization and teamwork.',
  'Allow students to take ownership of their learning by giving them choice and autonomy.',
  'Provide opportunities for students to apply what they have learned through projects, performance tasks, and real-world scenarios.',
  'Use formative assessment strategies regularly to check for understanding and adjust instruction accordingly.',
  'Foster a growth mindset in your students by emphasizing effort, progress, and perseverance rather than just grades or test scores.',
  'Use positive reinforcement and recognize students for their efforts and successes.',
  'Provide ongoing professional development and stay up to date with the latest teaching strategies and research in your field.',
  'Use inclusive language and avoid stereotypes to create a safe and welcoming environment for all students, regardless of their background or identity.',
  'Use a variety of assessment methods, such as written tests, projects, presentations, and portfolios, to provide a more comprehensive understanding of student learning.',
  'Provide opportunities for students to give feedback on their learning experience and incorporate their suggestions into your teaching practice.',
  'Encourage students to ask questions and be curious, and provide resources for them to explore topics that interest them.',
  'Emphasize the importance of academic integrity and teach students how to cite sources and avoid plagiarism.',
  'Use humor and storytelling to make lessons more engaging and memorable for students.',
  'Create a classroom culture of respect and kindness by modeling positive behavior and addressing any instances of bullying or negative interactions among students.',
  'Collaborate with other teachers and education professionals to share ideas and learn from one another, and to develop a strong sense of community within your school.',
  'Use as is, or edit and make it yours!',
  'Remember to fact check - AI can make mistakes',
  'Click Start lesson to see classroom view',
];

export const AnimatedTipsV2 = ({
  isSmallTemplates,
  darkMode = false,
}: {
  isSmallTemplates?: boolean;
  darkMode?: boolean;
}) => {
  const { t } = useTranslation('Host');
  const [selectedTipIndex, setSelectedTipIndex] = useState(0);
  const [randomisedTips, setRandomisedTips] = useState<string[] | null>();
  const isLargerThan600 = useBreakPoint(600);
  useEffect(() => {
    setRandomisedTips(shuffleArray(tips));
  }, []);

  useEffect(() => {
    if (randomisedTips) {
      if (selectedTipIndex < randomisedTips.length) {
        const timer = setInterval(() => setSelectedTipIndex(selectedTipIndex + 1), 10000);
        return () => clearInterval(timer);
      } else {
        setSelectedTipIndex(0);
      }
    }
  }, [selectedTipIndex, randomisedTips]);

  return (
    <AnimatedTipsContainer>
      {isLargerThan600 && (
        <h1
          style={{
            fontSize: 20,
            marginBottom: 20,
            textAlign: 'center',
            color: 'white',
          }}
        >
          {t('A wise teacher once said')}:{' '}
        </h1>
      )}
      {randomisedTips &&
        randomisedTips.map((tip, i) => (
          <>
            {i === selectedTipIndex && (
              <StyledTip
                $isVisible={i === selectedTipIndex}
                key={i}
                $isSmallTemplates={isSmallTemplates}
                style={{ color: 'white' }}
              >
                &ldquo;{tip}&rdquo;
              </StyledTip>
            )}
          </>
        ))}
    </AnimatedTipsContainer>
  );
};

const ProgressBarV2 = ({
  title = 'Customizing lesson',
  isSmallTemplates,
  darkMode = false,
}: {
  title?: string;
  isSmallTemplates?: boolean;
  darkMode?: boolean;
}) => {
  return (
    <>
      <h1
        style={{
          color: 'white',
        }}
      >
        {title}
        <LoadingDots>...</LoadingDots>
      </h1>
      <LoadingContainer>
        <ProgressLine>
          <CuriRocketSvg />
          <CuriRocketBigFlameSvg />
        </ProgressLine>
      </LoadingContainer>
      <AnimatedTipsV2 darkMode={darkMode} isSmallTemplates={isSmallTemplates} />
    </>
  );
};

export default ProgressBarV2;

const LoadingContainer = styled.div`
  width: 80%;
  padding: 10px;
  margin-top: 50px;

  @media (max-width: 800px) {
    margin-top: 50px;
    width: 70%;
  }
  @media (max-width: 450px) {
    margin-top: 30px;
    width: 75%;
  }
`;

const ProgressLine = styled.div`
  position: relative;
  width: 0%;
  height: 40px;
  transform: rotate(0deg);
  transform-origin: center;
  animation: progress 10s infinite ease-in-out,
    moveRocketUpAndDown 8s infinite ease-in-out;
  svg {
    height: 150px;
    width: 150px;
    position: absolute;
    right: -75px;
    top: -50px;
    transform: rotate(0deg);
    transform-origin: center;
    animation: rotateRocket 10s infinite ease-in-out;
  }

  svg:last-child {
    animation: flames 0.2s infinite linear, rotateRocket 10s infinite ease-in-out;
  }

  @media (max-width: 450px) {
    svg {
      height: 100px;
      width: 100px;
      right: -50px;
      top: -25px;
    }
  }

  @keyframes progress {
    0% {
      width: 10%;
    }

    50% {
      width: 90%;
    }

    100% {
      width: 10%;
    }
  }

  @keyframes rotateRocket {
    0% {
      transform: rotate(15deg);
    }
    50% {
      transform: rotate(-90deg);
    }

    100% {
      transform: rotate(15deg);
    }
  }

  @keyframes flames {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  @keyframes moveRocketUpAndDown {
    0% {
      transform: translateY(-0px);
    }
    25% {
      transform: translateY(75px);
    }
    50% {
      transform: translateY(25px);
    }
    75% {
      transform: translateY(75px);
    }

    100% {
      transform: translateY(-0px);
    }
  }
`;

const AnimatedTipsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 150px;

  width: 90%;
  margin-bottom: 150px;

  @media (max-width: 450px) {
    width: 90%;
    margin-top: 120px;
  }
`;

const StyledTip = styled.p<{ $isVisible: boolean; $isSmallTemplates?: boolean }>`
  opacity: 0;
  max-width: 300px;
  font-style: italic;
  min-height: 200px;
  font-size: ${({ $isSmallTemplates }) => ($isSmallTemplates ? '18px' : '22px')};
  text-align: center;
  animation: ${({ $isVisible }) => ($isVisible ? 'fadeIn 10s linear' : '')};

  p {
    margin: 0 auto;
  }

  @media (max-width: 600px) or (max-height: 600px) {
    font-size: 16px;

    h1 {
      font-size: 18px !important;
    }
  }

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    6% {
      opacity: 1;
    }
    94% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
`;
